"use client";

import { c as _c } from "react/compiler-runtime";
import { alpha, Box, type BoxProps, keyframes, type SxProps, type Theme, Typography, useTheme } from "@mui/material";
import clsx from "clsx";
import React, { useEffect, useRef } from "react";
import { type TreeNodeData, treeNodeSlice } from "#/entities/dataStructures/node/model/nodeSlice";
import { selectCallstackIsReady } from "#/features/callstack/model/callstackSlice";
import { useAppDispatch, useAppSelector } from "#/store/hooks";
const nodeSize = "42px";
const nodeProps: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  p: 1,
  width: nodeSize,
  height: nodeSize
};
const blinkKeyframes = keyframes`
  0% {
    transform: scale(1);
  }

  5% {
    transform: scale(1.3);
  }

  100% {
    transform: scale(1);
  }
`;
const pulseKeyframes = keyframes`
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.1);
  }

  100% {
    transform: scale(1);
  }
`;
export type NodeBaseProps = Pick<BoxProps, "style" | "onMouseDown" | "onMouseUp"> & TreeNodeData & {
  treeName: string;
  nodeColor: string;
  shadowColor: string;
  cursor?: "pointer" | "grab";
};
export const NodeBase: React.FC<NodeBaseProps> = t0 => {
  const $ = _c(53);
  const {
    id,
    treeName,
    value,
    nodeColor,
    shadowColor,
    animation,
    animationCount,
    isHighlighted,
    x,
    y,
    cursor: t1,
    onMouseDown,
    onMouseUp
  } = t0;
  const cursor = t1 === undefined ? "pointer" : t1;
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const nodeRef = useRef(null);
  const overlayRef = useRef(null);
  const isCallstackReady = useAppSelector(selectCallstackIsReady);
  let t2;
  if ($[0] !== dispatch || $[1] !== id || $[2] !== treeName) {
    t2 = () => {
      dispatch(treeNodeSlice.actions.triggerAnimation({
        name: treeName,
        data: {
          id,
          animation: "blink"
        }
      }));
    };
    $[0] = dispatch;
    $[1] = id;
    $[2] = treeName;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleBlink = t2;
  let t3;
  let t4;
  if ($[4] !== animation || $[5] !== animationCount) {
    t3 = () => {
      if (!animation || animationCount === undefined) {
        return;
      }
      const element = nodeRef.current;
      if (element) {
        element.classList.remove(animation);
        element.classList.add(animation);
      }
      return () => {
        if (element) {
          element.classList.remove(animation);
        }
      };
    };
    t4 = [animation, animationCount];
    $[4] = animation;
    $[5] = animationCount;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  const t5 = isCallstackReady ? "all .05s" : "none";
  let t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      animation: `${blinkKeyframes} 0.24s ease-out`
    };
    t7 = {
      animation: `${pulseKeyframes} 0.5s ease-out infinite`
    };
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t5) {
    t8 = {
      position: "absolute",
      zIndex: 1,
      width: "fit-content",
      transition: t5,
      ".blink": t6,
      ".pulse": t7
    };
    $[10] = t5;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== x || $[13] !== y) {
    t9 = {
      left: x,
      top: y
    };
    $[12] = x;
    $[13] = y;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      opacity: "0.1!important"
    };
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== cursor) {
    t11 = {
      position: "relative",
      "&:hover": {
        cursor,
        "& > .node-overlay": t10
      }
    };
    $[16] = cursor;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const t12 = String(value);
  let t13;
  if ($[18] !== nodeColor) {
    t13 = alpha(nodeColor, 0.3);
    $[18] = nodeColor;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== nodeColor) {
    t14 = alpha(nodeColor, 0.22);
    $[20] = nodeColor;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const t15 = `1px solid ${t14}`;
  let t16;
  if ($[22] !== shadowColor) {
    t16 = alpha(shadowColor, 0.5);
    $[22] = shadowColor;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  const t17 = `0px 0px 18px -2px ${t16}`;
  let t18;
  if ($[24] !== t13 || $[25] !== t15 || $[26] !== t17 || $[27] !== theme.palette.primary.contrastText) {
    t18 = {
      ...nodeProps,
      transition: "all .2s",
      borderRadius: "50%",
      background: t13,
      border: t15,
      backdropFilter: "blur(4px)",
      userSelect: "none",
      boxShadow: t17,
      color: theme.palette.primary.contrastText
    };
    $[24] = t13;
    $[25] = t15;
    $[26] = t17;
    $[27] = theme.palette.primary.contrastText;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== handleBlink || $[30] !== t12 || $[31] !== t18) {
    t19 = <Box component="div" onClick={handleBlink} title={t12} sx={t18} />;
    $[29] = handleBlink;
    $[30] = t12;
    $[31] = t18;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      position: "absolute",
      top: "50%",
      left: 0,
      transform: "translateY(-50%)",
      width: "100%",
      textAlign: "center",
      overflow: "hidden",
      pointerEvents: "none",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      padding: "0 4px"
    };
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== value) {
    t21 = <Typography component="span" color="white" sx={t20}>{value}</Typography>;
    $[34] = value;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = clsx("node-overlay");
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  const t23 = isHighlighted ? 0.1 : 0;
  let t24;
  if ($[37] !== t23) {
    t24 = <Box ref={overlayRef} className={t22} sx={{
      position: "absolute",
      top: 0,
      left: 0,
      height: "100%",
      width: "100%",
      borderRadius: "50%",
      background: "white",
      pointerEvents: "none",
      transition: "opacity .1s",
      opacity: t23
    }} />;
    $[37] = t23;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] !== t11 || $[40] !== t19 || $[41] !== t21 || $[42] !== t24) {
    t25 = <Box ref={nodeRef} sx={t11}>{t19}{t21}{t24}</Box>;
    $[39] = t11;
    $[40] = t19;
    $[41] = t21;
    $[42] = t24;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] !== nodeColor) {
    t26 = <Box sx={{
      position: "absolute",
      zIndex: -1,
      top: "13px",
      left: "50%",
      transform: "translate(-50%, 0)",
      width: "18px",
      height: "18px",
      borderRadius: "50%",
      background: nodeColor
    }} />;
    $[44] = nodeColor;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  let t27;
  if ($[46] !== onMouseDown || $[47] !== onMouseUp || $[48] !== t25 || $[49] !== t26 || $[50] !== t8 || $[51] !== t9) {
    t27 = <Box sx={t8} style={t9} onMouseDown={onMouseDown} onMouseUp={onMouseUp}>{t25}{t26}</Box>;
    $[46] = onMouseDown;
    $[47] = onMouseUp;
    $[48] = t25;
    $[49] = t26;
    $[50] = t8;
    $[51] = t9;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  return t27;
};